import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/homeData';

export default function CategorySection() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
            Browse Product Categories
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-lg mx-auto">
            Select a category below to explore products on the Basetao Spreadsheet platform
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div ref={dropdownRef} className="relative w-full max-w-md">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between gap-3 px-5 py-3.5 bg-white border border-background-200 rounded-lg text-foreground-800 text-sm font-medium hover:border-primary-300 transition-colors duration-200 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-menu-2-line text-lg text-primary-500"></i>
                {isOpen ? 'Select a Category' : 'Browse All Categories'}
              </span>
              <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 transition-transform duration-200`}></i>
            </button>

            {isOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-background-200 rounded-lg overflow-hidden z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer border-b border-background-100 last:border-b-0"
                  >
                    <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col overflow-hidden bg-white border border-background-200 hover:border-primary-200 rounded-lg transition-all duration-200 cursor-pointer"
            >
              <div className="w-full aspect-square overflow-hidden bg-background-100">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3 text-center">
                <span className="text-xs text-foreground-700 group-hover:text-foreground-900 font-medium leading-tight">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}